import React from "react";
import { motion, MotionProps } from "framer-motion";
import { useInView } from "react-intersection-observer";

type Entry =
  | "fadeIn"
  | "fade"
  | "zoom"
  | "slide-up"
  | "slide-down"
  | "slide-left"
  | "slide-right"
  | "skewReveal"
  | "flipIn"
  | "elasticLeft"
  | "blurReveal"
  | "dropIn";

interface AnimatedBoxProps extends Partial<MotionProps> {
  children: React.ReactNode;
  entry?: Entry;
  duration?: number;
  delay?: number;
  className?: string;
  style?: React.CSSProperties;
  triggerOnce?: boolean;
  threshold?: number;
}

const configs: Record<Entry, Partial<MotionProps>> = {
  fadeIn: {
    initial: { opacity: 0, scale: 0.8 },
    animate: { opacity: 1, scale: 1 },
  },
  fade: {
    initial: { opacity: 0.5 },
    animate: { opacity: 1 },
    transition: { duration: 0.5 },
  },
  zoom: {
    initial: { opacity: 0, scale: 0.8 },
    animate: { opacity: 1, scale: 1 },
  },
  "slide-up": { initial: { opacity: 0, y: 40 }, animate: { opacity: 1, y: 0 } },
  "slide-down": {
    initial: { opacity: 0, y: -40 },
    animate: { opacity: 1, y: 0 },
  },
  "slide-right": {
    initial: { x: 300, opacity: 0 },
    animate: { x: 0, opacity: 1 },
    transition: { duration: 0.4, ease: "easeOut" },
  },
  "slide-left": {
    initial: { x: -300, opacity: 0 },
    animate: { x: 0, opacity: 1 },
    transition: { duration: 0.4, ease: "easeIn" },
  },
  skewReveal: {
    initial: { opacity: 0, y: 30, skewY: -6 },
    animate: { opacity: 1, y: 0, skewY: 0 },
    transition: { duration: 0.5 },
  },
  flipIn: {
    initial: { opacity: 0, rotateX: -90, transformPerspective: 800 },
    animate: { opacity: 1, rotateX: 0 },
    transition: { type: "spring", stiffness: 120, damping: 14 },
  },
  elasticLeft: {
    initial: { opacity: 0, x: -90 },
    animate: { opacity: 1, x: 0 },
    transition: { type: "spring", stiffness: 100, damping: 8 },
  },
  blurReveal: {
    initial: { opacity: 0, filter: "blur(8px)" },
    animate: { opacity: 1, filter: "blur(0px)" },
    transition: { duration: 0.45 },
  },
  dropIn: {
    initial: { opacity: 0, y: -120, scale: 0.95 },
    animate: { opacity: 1, y: 0, scale: 1 },
    transition: { type: "spring", stiffness: 160, damping: 18 },
  },
};

export default function AnimatedBox({
  children,
  entry = "fadeIn",
  duration = 0.5,
  delay = 0,
  className,
  style,
  triggerOnce = true,
  threshold = 0.25,
  ...rest
}: AnimatedBoxProps) {
  const base = configs[entry] ?? configs.fadeIn;

  const [ref, inView] = useInView({ triggerOnce, threshold });

  const finalTransition = base.transition ?? { duration, delay };

  return (
    <motion.div
      ref={ref}
      initial={base.initial}
      animate={inView ? base.animate : base.initial}
      transition={finalTransition}
      className={className}
      style={style}
      {...rest}
    >
      {children}
    </motion.div>
  );
}
